import { useMemo, useState } from "react";
import { Maximize2, Search } from "lucide-react";
import { fmtUSD } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUS = { Completed: "text-emerald-700 border-emerald-500/30", Pending: "text-amber-700 border-amber-500/40", "AML Hold": "text-red-700 border-red-500/50 bg-red-500/10" };
const DIR = { Incoming: "text-sky-700", Outgoing: "text-amber-700", Internal: "text-violet-700" };
const COLS = ["Date", "Reference", "Direction", "Status", "Source", "Account", "Client", "Bank", "Originator", "Originator A/C", "Beneficiary", "Beneficiary A/C", "Amount", ""];
const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
const Party = ({ name, ctry }) => <span className="flex items-center gap-1.5 max-w-[190px]"><span className="truncate">{name}</span>{ctry && <span className="text-[9px] text-slate-400 font-semibold">{ctry}</span>}</span>;

function Rows({ rows, onOpenAlert }) {
  return (
    <table className="w-full text-xs">
      <thead className="sticky top-0 bg-white z-10"><tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">{COLS.map((h, i) => <th key={i} className={`px-3 py-2.5 font-semibold whitespace-nowrap ${h === "Amount" ? "text-right" : ""}`}>{h}</th>)}</tr></thead>
      <tbody>
        {rows.map((w) => (
          <tr key={w.id} data-testid={`wire-row-${w.id}`} className={`border-t border-slate-100 hover:bg-sky-50/50 transition-colors ${w.aml_review ? "bg-amber-500/[0.04]" : ""}`}>
            <td className="px-3 py-2 num text-slate-600 whitespace-nowrap">{fmtDate(w.timestamp)}</td>
            <td className="px-3 py-2 num text-slate-700 whitespace-nowrap">{w.id}</td>
            <td className={`px-3 py-2 font-medium ${DIR[w.direction]}`}>{w.direction}</td>
            <td className="px-3 py-2"><span className={`text-[10px] px-1.5 py-0.5 rounded border whitespace-nowrap ${STATUS[w.status]}`}>{w.status}</span></td>
            <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{w.source}</td>
            <td className="px-3 py-2 num text-slate-600">{w.account}</td>
            <td className="px-3 py-2 text-slate-900 font-medium whitespace-nowrap max-w-[180px] truncate">{w.client}</td>
            <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{w.bank}</td>
            <td className="px-3 py-2 text-slate-700"><Party name={w.originator} ctry={w.originator_country} /></td>
            <td className="px-3 py-2 num text-slate-500 max-w-[150px] truncate">{w.originator_account}</td>
            <td className="px-3 py-2 text-slate-700"><Party name={w.beneficiary} ctry={w.beneficiary_country} /></td>
            <td className="px-3 py-2 num text-slate-500 max-w-[150px] truncate">{w.beneficiary_account}</td>
            <td className="px-3 py-2 num text-right text-slate-900 whitespace-nowrap">{fmtUSD(w.amount)}</td>
            <td className="px-3 py-2">{w.alert_id && <button data-testid={`wire-alert-link-${w.id}`} onClick={() => onOpenAlert(w.alert_id)} title={w.alert_id} className="num text-[10px] text-sky-600 hover:text-sky-800 underline underline-offset-2 whitespace-nowrap">Alert #{w.alert_id.slice(-4)}</button>}</td>
          </tr>
        ))}
        {!rows.length && <tr><td colSpan={COLS.length} className="px-3 py-6 text-center text-slate-500">No wires match.</td></tr>}
      </tbody>
    </table>
  );
}

export function WireTable({ rows, onOpenAlert, limit, source }) {
  const [q, setQ] = useState("");
  const [big, setBig] = useState(false);
  const hits = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? rows.filter((w) => Object.values(w).some((v) => v != null && String(v).toLowerCase().includes(s))) : rows;
  }, [rows, q]);
  const shown = limit ? hits.slice(0, limit) : hits;
  return (
    <div className="panel overflow-hidden" data-testid="wire-table">
      <div className="px-5 py-3.5 border-b border-slate-100 flex flex-wrap gap-3 justify-between items-center">
        <div><div className="text-sm font-semibold text-slate-900">Individual wires</div><div className="text-[11px] text-slate-500">One row per wire, newest first · <span data-testid="wire-table-count" className="num">{hits.length}</span> rows</div></div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-200 bg-white focus-within:border-sky-400 transition-colors">
            <Search className="w-3.5 h-3.5 text-slate-400" />
            <input data-testid="wire-table-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find in any column" className="text-xs outline-none w-44 bg-transparent" />
          </label>
          <button data-testid="wire-table-expand" onClick={() => setBig(true)} className="btn-ghost"><Maximize2 className="w-3.5 h-3.5" />Expand</button>
        </div>
      </div>
      <div className="overflow-x-auto thin-scroll"><Rows rows={shown} onOpenAlert={onOpenAlert} /></div>
      {limit && hits.length > limit && <div className="px-5 py-2 text-[11px] text-slate-500 border-t border-slate-100">Showing {limit} of {hits.length} · use Expand or the Transactions tab for all rows</div>}
      {source && <SourceFooter source={source} />}
      <Dialog open={big} onOpenChange={setBig}>
        <DialogContent className="max-w-[96vw] w-[96vw] h-[88vh] flex flex-col bg-white" data-testid="wire-table-dialog">
          <DialogHeader><DialogTitle>Individual wires · {hits.length} rows</DialogTitle></DialogHeader>
          <div className="flex-1 min-h-0 overflow-auto thin-scroll border border-slate-100 rounded-lg"><Rows rows={hits} onOpenAlert={(id) => { setBig(false); onOpenAlert(id); }} /></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function SourceFooter({ source }) {
  return (
    <div data-testid="data-source-footer" className="px-5 py-2.5 border-t border-slate-100 text-[10px] num uppercase tracking-wider text-slate-400">
      {source.system} · as of {new Date(source.as_of).toLocaleDateString("en-US")} {new Date(source.as_of).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
    </div>
  );
}
